"use client";

import { FunctionComponent, useCallback, useEffect } from "react";

import { productImageNotFoundImagePath } from "@/app/ec/product/productImage";

import Script from "next/script";
import styles from "./questionsAndAnswers.module.scss";

export const YOTPO_KEY = "k0ISk4VuVmhyvM3k5lrJdmv4nfZwZ4QfG4gpdvqH";

export const QuestionsAndAnswersSection: FunctionComponent<Props> = ({ mpId, productDetails, bestVendorOption }) => {
  const productLink =
    process.env.NEXT_PUBLIC_BASE_URL +
    (productDetails.url.charAt(0) === "/" ? productDetails.url : "/" + productDetails.url);
  const mediaLink = productDetails.mediaPath
    ? process.env.NEXT_PUBLIC_BASE_URL +
      (productDetails.mediaPath?.charAt(0) === "/" ? productDetails.mediaPath : "/" + productDetails.mediaPath)
    : process.env.NEXT_PUBLIC_BASE_URL + productImageNotFoundImagePath;
  const bestVendorOptionPrice = bestVendorOption ? bestVendorOption.productSubTotal : "";
  const yotpoLink = `//staticw2.yotpo.com/${YOTPO_KEY}/widget.js`;

  const refreshYotpo = useCallback(() => {
    try {
      if (window.yotpo && window.yotpo.refreshWidgets) {
        window.yotpo.refreshWidgets();
      } else {
        setTimeout(() => {
          refreshYotpo();
        }, 250);
      }
    } catch (error) {
      console.error("Error when refreshing yotpo", error);
    }
  }, []);
  useEffect(() => {
    if (!document.querySelector("#q-and-a > div.yotpo.yotpo-main-widget > div")) {
      refreshYotpo();
    }
  }, [refreshYotpo]);

  // Rewrite Yotpo's third-party copy, re-applying on every widget re-render.
  useEffect(() => {
    const container = document.getElementById("q-and-a");
    if (!container) return;

    // Disable the "Post Question" submit until every required field (question, name, email) has text.
    const syncSubmitDisabled = () => {
      container.querySelectorAll<HTMLInputElement>(".yotpo-submit").forEach((submit) => {
        const form = submit.closest("form");
        if (!form) return;
        const required = Array.from(
          form.querySelectorAll<HTMLInputElement | HTMLTextAreaElement>('[aria-required="true"]'),
        );
        if (required.length === 0) return;
        submit.disabled = !required.every((field) => field.value.trim().length > 0);
      });
    };

    const rewrite = () => {
      const walker = document.createTreeWalker(container, NodeFilter.SHOW_TEXT);
      const textNodes: Text[] = [];
      while (walker.nextNode()) textNodes.push(walker.currentNode as Text);

      textNodes.forEach((node) => {
        const value = node.nodeValue ?? "";

        // "N Questions \ N Answers" -> "N Questions / N Answers"
        if (value.includes("\\")) {
          const parentText = node.parentElement?.textContent ?? "";
          const inCount =
            (/Questions/i.test(value) && /Answers/i.test(value)) ||
            (value.trim() === "\\" && /Questions/i.test(parentText) && /Answers/i.test(parentText));
          if (inCount) {
            node.nodeValue = value.replace(/\\/g, "/");
            return;
          }
        }

        // "Use your name:" -> "Name:"
        if (/use your name/i.test(value)) {
          node.nodeValue = "Name:";
        }
      });

      // "Question:" label above the textarea
      const questionLabel = container.querySelector(".write-question-content .form-group .form-element .y-label");
      if (questionLabel && questionLabel.textContent?.trim() !== "Question:") {
        questionLabel.textContent = "Question:";
      }

      // "Post" -> "Post Question"
      const submit = container.querySelector<HTMLElement>(".yotpo-submit");
      if (submit) {
        if (submit instanceof HTMLInputElement) {
          if (submit.value.trim() === "Post") submit.value = "Post Question";
        } else if (submit.textContent?.trim() === "Post") {
          submit.textContent = "Post Question";
        }
      }

      // Q&A rows: the question author label reads "{Name} asked:", and the "Q:"/"A:" prefixes
      // are stripped from the bodies. The answer label is rendered from the stylesheet.
      container.querySelectorAll(".yotpo-question").forEach((q) => {
        const qName = q.querySelector<HTMLElement>(":scope > .yotpo-header .yotpo-user-name");
        if (qName) {
          const t = qName.textContent?.trim() ?? "";
          if (t && !/asked:$/.test(t)) qName.textContent = `${t.replace(/:$/, "")} asked:`;
        }
      });
      container.querySelectorAll(".content-question").forEach((el) => {
        const first = el.firstChild;
        if (first && first.nodeType === Node.TEXT_NODE) {
          const v = first.nodeValue ?? "";
          const m = v.match(/^\s*[QA]:\s*/);
          if (m) first.nodeValue = v.slice(m[0].length);
        }
      });

      syncSubmitDisabled();
    };

    let scheduled = false;
    const schedule = () => {
      if (scheduled) return;
      scheduled = true;
      requestAnimationFrame(() => {
        scheduled = false;
        rewrite();
      });
    };

    rewrite();
    container.addEventListener("input", syncSubmitDisabled);
    const observer = new MutationObserver(schedule);
    observer.observe(container, { childList: true, subtree: true, characterData: true });
    return () => {
      observer.disconnect();
      container.removeEventListener("input", syncSubmitDisabled);
    };
  }, []);

  if (!mpId || !productDetails) return null;

  return (
    <>
      {<Script strategy="afterInteractive" type="text/javascript" src={yotpoLink} />}
      <div id="q-and-a" className={styles.QAndA}>
        <div className="pt-4 pb-2 q-and-a-header">Customer Questions &amp; Answers</div>

        <div
          className="yotpo yotpo-main-widget"
          data-product-id={mpId}
          data-price={bestVendorOptionPrice}
          data-currency="USD"
          data-name={productDetails.title}
          data-url={productLink}
          data-image-url={mediaLink}
          data-description={productDetails.description}
          data-mode="questions"
        />
      </div>
    </>
  );
};

type Props = {
  mpId: number | null;
  productDetails: ProductDetails;
  bestVendorOption: VendorOptionState;
};
export default QuestionsAndAnswersSection;
